import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const GeographicDistribution = ({ data, impressionsData }) => {
  const [displayCount, setDisplayCount] = useState(10); // 10 means show top 10
  const [metricType, setMetricType] = useState("absolute"); // 'absolute' or 'percentage'
  const [dateIndex, setDateIndex] = useState(-1); // -1 means aggregate all dates
  const [dataType, setDataType] = useState("clicks"); // 'clicks' or 'impressions'

  // Format date for display
  const formatDate = (dateStr) => {
    // Convert "2025-01-17" to "1/17/2025"
    const [year, month, day] = dateStr.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const availableDates = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...new Set(data.map((item) => item.date))].sort(
      (a, b) => new Date(a) - new Date(b)
    );
  }, [data]);

  const currentDate = dateIndex >= 0 ? availableDates[dateIndex] : "aggregate";

  // Use the selected data type
  const activeData =
    dataType === "impressions" && impressionsData ? impressionsData : data;

  const processedData = useMemo(() => {
    if (!activeData || activeData.length === 0) return null;

    let countriesMap = new Map();

    if (currentDate === "aggregate") {
      // Aggregate data across all dates
      activeData.forEach((dateData) => {
        Object.entries(dateData)
          .filter(([key]) => key !== "date")
          .forEach(([country, value]) => {
            if (value !== null && value !== 0 && value !== "0") {
              const currentValue = countriesMap.get(country) || 0;
              countriesMap.set(country, currentValue + Number(value));
            }
          });
      });
    } else {
      // Get data for the selected date
      const dateData = activeData.find((item) => item.date === currentDate);
      if (!dateData) return null;

      // Process data for a single date
      Object.entries(dateData)
        .filter(([key]) => key !== "date")
        .filter(([_, value]) => value !== null && value !== 0 && value !== "0")
        .forEach(([country, value]) => {
          countriesMap.set(country, Number(value));
        });
    }

    // Convert map to array and sort
    let countries = Array.from(countriesMap.entries()).sort(
      (a, b) => b[1] - a[1]
    );

    const total = countries.reduce((sum, [_, value]) => sum + value, 0);

    // If displayCount is 0, show all countries, otherwise show top N
    if (displayCount > 0) {
      countries = countries.slice(0, displayCount);
    }

    return {
      countries,
      total,
    };
  }, [activeData, currentDate, displayCount]);

  const chartData = useMemo(() => {
    if (!processedData) return null;

    const labels = processedData.countries.map(([country]) => country);
    const values = processedData.countries.map(([_, value]) => {
      if (metricType === "percentage") {
        return ((value / processedData.total) * 100).toFixed(1);
      }
      return value;
    });

    // Generate a color palette
    const generateColors = (count) => {
      const baseColors = [
        "rgba(54, 162, 235, 0.7)", // Blue
        "rgba(255, 99, 132, 0.7)", // Red
        "rgba(75, 192, 192, 0.7)", // Green
        "rgba(255, 159, 64, 0.7)", // Orange
        "rgba(153, 102, 255, 0.7)", // Purple
        "rgba(255, 205, 86, 0.7)", // Yellow
        "rgba(201, 203, 207, 0.7)", // Grey
        "rgba(54, 162, 235, 0.5)", // Light Blue
        "rgba(255, 99, 132, 0.5)", // Light Red
        "rgba(75, 192, 192, 0.5)", // Light Green
      ];

      // If we need more colors than our base palette, generate them
      if (count > baseColors.length) {
        const additionalColors = Array(count - baseColors.length)
          .fill()
          .map(() => {
            return `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
              Math.random() * 255
            )}, ${Math.floor(Math.random() * 255)}, 0.7)`;
          });
        return [...baseColors, ...additionalColors];
      }

      return baseColors.slice(0, count);
    };

    return {
      labels,
      datasets: [
        {
          label: metricType === "percentage" ? "% of Total" : "Count",
          data: values,
          backgroundColor: generateColors(labels.length),
          borderColor: "rgba(255, 255, 255, 0.2)",
          borderWidth: 1,
        },
      ],
    };
  }, [processedData, metricType]);

  const totalEntries = processedData?.total || 0;
  const totalCountries = processedData?.countries.length || 0;

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <h3 className="text-lg font-medium">No Geographic Data Available</h3>
          <p className="mt-2">
            Please upload geographic distribution data to view this chart.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-200">
            Geographic Distribution
          </h2>
          <p className="text-sm text-gray-400">Analyzing audience by country</p>
        </div>

        <div className="flex flex-wrap gap-4">
          <div>
            <label
              htmlFor="date-select"
              className="block text-sm font-medium text-gray-400 mb-1"
            >
              Date
            </label>
            <select
              id="date-select"
              value={dateIndex}
              onChange={(e) => setDateIndex(Number(e.target.value))}
              className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={-1}>Aggregate (All Dates)</option>
              {availableDates.map((date, index) => (
                <option key={date} value={index}>
                  {formatDate(date)}
                </option>
              ))}
            </select>
          </div>

          {impressionsData && (
            <div>
              <label
                htmlFor="data-type-select"
                className="block text-sm font-medium text-gray-400 mb-1"
              >
                Data Type
              </label>
              <select
                id="data-type-select"
                value={dataType}
                onChange={(e) => setDataType(e.target.value)}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="clicks">Clicks</option>
                <option value="impressions">Impressions</option>
              </select>
            </div>
          )}

          <div>
            <label
              htmlFor="count-select"
              className="block text-sm font-medium text-gray-400 mb-1"
            >
              Show Top
            </label>
            <select
              id="count-select"
              value={displayCount}
              onChange={(e) => setDisplayCount(Number(e.target.value))}
              className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>Top 10</option>
              <option value={0}>Show All</option>
              <option value={5}>Top 5</option>
              <option value={15}>Top 15</option>
              <option value={20}>Top 20</option>
              <option value={50}>Top 50</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="metric-select"
              className="block text-sm font-medium text-gray-400 mb-1"
            >
              Metric Type
            </label>
            <select
              id="metric-select"
              value={metricType}
              onChange={(e) => setMetricType(e.target.value)}
              className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="absolute">Absolute Values</option>
              <option value="percentage">Percentage</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 p-6 rounded-lg">
        {chartData ? (
          <>
            <div className="mb-6">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <span className="text-sm text-gray-400">
                  Date:{" "}
                  <span className="font-medium text-gray-300">
                    {currentDate === "aggregate"
                      ? "All Dates (Aggregated)"
                      : formatDate(currentDate)}
                  </span>
                </span>
                <span className="text-sm text-gray-400">
                  Data Type:{" "}
                  <span className="font-medium text-gray-300">
                    {dataType.charAt(0).toUpperCase() + dataType.slice(1)}
                  </span>
                </span>
                <span className="text-sm text-gray-400">
                  Total:{" "}
                  <span className="font-medium text-gray-300">
                    {totalEntries.toLocaleString()}
                  </span>
                </span>
              </div>
            </div>

            <div className="h-96">
              <Bar
                data={chartData}
                options={{
                  indexAxis: "y",
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "top",
                      labels: {
                        color: "#E5E7EB",
                      },
                    },
                    title: {
                      display: true,
                      text: `Geographic Distribution - ${
                        dataType.charAt(0).toUpperCase() + dataType.slice(1)
                      }`,
                      color: "#E5E7EB",
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const value = context.raw;
                          const total = processedData.total;

                          if (metricType === "percentage") {
                            return `${value}% (${context.dataset.data[
                              context.dataIndex
                            ].toLocaleString()})`;
                          } else {
                            return `${value.toLocaleString()} (${(
                              (value / total) *
                              100
                            ).toFixed(1)}%)`;
                          }
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      ticks: {
                        color: "#E5E7EB",
                        callback: function (value) {
                          if (value >= 1000) {
                            return (value / 1000).toFixed(1) + "k";
                          }
                          return value;
                        },
                      },
                      grid: {
                        color: "rgba(255, 255, 255, 0.1)",
                      },
                    },
                    y: {
                      ticks: {
                        color: "#E5E7EB",
                      },
                      grid: {
                        color: "rgba(255, 255, 255, 0.1)",
                      },
                    },
                  },
                }}
              />
            </div>
          </>
        ) : (
          <div className="h-96 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <p>No data available for the selected date.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeographicDistribution;
